import React, { useState } from "react";
import styled from "styled-components";
import TextView from "../../../TextView";
import { ASSETS } from "../../../../utils/config";
import { Pane } from "../../Toolbar/styles";
import Button from "../../../Button";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import {
  getWheelAutoResizing,
  getWheelSegments,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import { useDispatch } from "react-redux";
import useSegmentSettings from "../../../../hooks/useSegmentSetting";
import { newSegment } from "../../../../constants/wheelData";
import {
  handleMenuChange,
  pushToHistoryAction,
  setDesignTabTarget,
  setWheelSegments,
} from "../../../../redux/reducers/segmentBuilder";
import { countAllNodes } from "../../Main/SegmentsTab/SegmentsTabHelpers";
import ConfirmationModal from "../../Main/BusinessBoxBuilder/children/ConfirmationModal";
import { useSegmentPalettes } from "../../../../hooks/useSegmentPalettes";

const Header = styled.div`
  display: flex;
  border-bottom: 1px solid #ded8e1;
  padding-bottom: 8px;
  padding-left: 10px;
`;

const Content = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
`;

const ContentInner = styled.div`
  width: 20vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const Image = styled.img`
  width: 100px;
  height: 100px;
`;

export function SidePaneContent() {
  const wheelData = useSelector(getWheelSegments);
  const automaticResizing = useSelector(getWheelAutoResizing);
  const [lockWarningModalOpen, setLockWarningModalOpen] = useState(false);
  const { fetchSegmentPalette } = useSegmentPalettes();

  const {
    getColor,
    handleChange,
    getMaxPosition,
    handleRotationData,
    generatePayload,
  } = useSegmentSettings() || {};
  const dispatch = useDispatch();

  const addSegment = () => {
    const allLocked =
      wheelData.length > 0 && wheelData.every((sb) => sb.resize_lock);
    const unlockedSegments = wheelData.filter((sb) => !sb.resize_lock);
    unlockedSegments.sort((a, b) => b.value - a.value);

    // If everything is locked or the biggest unlocked segment is ≤ 1, do nothing
    if (
      allLocked ||
      (unlockedSegments.length === 1 && unlockedSegments[0].value <= 1) ||
      (unlockedSegments.length > 0 && unlockedSegments[0].value <= 1)
    ) {
      setLockWarningModalOpen(true);
      return;
    }

    const newWheelData = wheelData.map((d, i) => ({
      ...d,
      position: typeof d.position === "number" ? d.position : (i + 1) * 10,
    }));

    const id = newWheelData.length + 1;


    const { color, colorInner } = getColor(newWheelData,newWheelData.length);

    const lastPosition = getMaxPosition(newWheelData);

    const currIndex = newWheelData.findIndex((e) => e.selected);
    let currentPos = 10;
    if (currIndex !== -1) {
      const nextIndex = currIndex + 1;
      currentPos =
        nextIndex < newWheelData.length
          ? newWheelData[nextIndex].position
          : lastPosition + 10;
    } else if (newWheelData.length > 0) {
      currentPos = lastPosition + 10;
    }

    const updatedWheelData = newWheelData.map((d, i) => ({
      ...d,
      selected: false,
      position: currIndex > -1 && i > currIndex ? d.position + 10 : d.position,
    }));

    // there is no currIndex ( means none of the index has selected true) all of them are false.
    //then the current index should be the last item. newWheelData.lenght

    const adjustedIndex =
      currIndex !== -1 ? currIndex : newWheelData.length - 1;

    if (automaticResizing) {
      // Gather code-block counts for *existing* segments
      let totalNodeCount = 0;
      const existingSegmentsWithCounts = updatedWheelData.map((seg) => {
        const nodeMap =
          typeof seg.nodes === "string" ? JSON.parse(seg.nodes) : seg.nodes;
        const nodeCount = countAllNodes(nodeMap, "ROOT"); // implement countAllNodes as needed
        totalNodeCount += nodeCount;
        return { ...seg, node_count: nodeCount };
      });

      // For our new (empty) segment, define at least 1 node if you want
      const newSegmentNodeCount = 1; // or more if you have default blocks
      totalNodeCount += newSegmentNodeCount;

      // Compute the fraction => new segment’s value
      const ratioForNew = newSegmentNodeCount / totalNodeCount;
      const finalValue = Number((ratioForNew * 100).toFixed(2));

      // Now splice the new segment with its computed finalValue
      updatedWheelData.splice(
        adjustedIndex + 1,
        0,
        newSegment({
          id,
          currentPos,
          color,
          colorInner,
          newSegmentValue: finalValue,
        })
      );
    } else {
      // PROPORTIONALLY REDUCE UNLOCKED SEGMENTS BEFORE INSERTING NEW ONE
      //    ---------------------------------------------------------------
      //  e.g., If the biggest unlocked segment is 50% of the total unlocked,
      //  we reduce it by ~50% of whatever space the new segment needs.
      const unlockedSum = unlockedSegments.reduce(
        (sum, sb) => sum + sb.value,
        0
      );

      // Distribute the new segment value so that all unlocked segments
      // share the “loss” of those points.
      const newSegmentValue = Number(
        Math.floor(unlockedSum / (unlockedSegments.length + 1)).toFixed(2)
      );
      if (newSegmentValue < 1 && unlockedSegments.length > 0) return; // Don’t allow sub-1 segments

      let finalValue = Math.max(1, newSegmentValue);

      // Everyone loses enough total to “give” newSegmentValue to the new segment
      const valueToReduce = finalValue;
      updatedWheelData.forEach((sb) => {
        if (!sb.resize_lock) {
          const reductionRatio = sb.value / unlockedSum;
          const reduction = Math.floor(valueToReduce * reductionRatio);
          sb.value = Number(Math.max(1, sb.value - reduction).toFixed(2));
        }
      });

      updatedWheelData.splice(
        adjustedIndex + 1,
        0,
        newSegment({
          id,
          currentPos,
          color,
          colorInner,
          newSegmentValue: finalValue,
        })
      );
    }
    let totalData = updatedWheelData.reduce((sum, s) => sum + s.data_value, 0);

    const finalSegments = updatedWheelData.map((seg) => {
      const newDataValue = (seg.value / 100) * totalData;
      return {
        ...seg,
        data_value: Math.max(1, Math.floor(newDataValue)),
      };
    });

    fetchSegmentPalette(color, id);
    dispatch(pushToHistoryAction()); // Push the new state to history
    dispatch(setWheelSegments(finalSegments));
  };

  const handleAddSegmentAfterAllLocked = () => {
    dispatch(handleMenuChange({ num: 1 })); // Open design tab
    dispatch(setDesignTabTarget("segments"));
  };
  return (
    <>
      <Content>
        <ContentInner>
          <Image
            src={ASSETS.segmentEditor.SegmentSampleIcon}
            alt="Segment Icon"
          />
          <TextView variant="text" style={{ textAlign: "center" }}>
            {
              "Select a segment on your wheel or create new - for the settings to show!"
            }
          </TextView>
          <Button
            onClick={addSegment}
            variant={"outlined"}
            style={{ width: 180, borderRadius: 100, margin: "auto" }}
          >
            <AddIcon /> Add Segment
          </Button>
        </ContentInner>
      </Content>

      <ConfirmationModal
        isOpen={lockWarningModalOpen}
        handleClose={() => setLockWarningModalOpen(false)}
        onConfirm={handleAddSegmentAfterAllLocked}
        title="Can't Add a Segment"
        message="All segments on this wheel are size-locked, so there's no space for a new one. To add a segment, unlock one or more existing segments."
        confirmText="Proceed"
      />
    </>
  );
}

const DefaultSidePane = ({ title, text, titleImage, selectedArc }) => {
  return (
    <Pane isVisible={!selectedArc}>
      <Header style={{ marginTop: 10 }}>
        <img src={titleImage} width={32} height={32} alt="Edit Icon" />
        <TextView variant="title" style={{ padding: "5px 0px 0px 10px" }}>
          {title}
        </TextView>
      </Header>

      <SidePaneContent />
    </Pane>
  );
};

export default DefaultSidePane;
